"use client";
import { User, Phone, X, MoveRight } from "lucide-react";
import { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import { API } from "aws-amplify";
import { Context } from "../../../../Context";
import PrintKOT from "./PrintKOT";
import PrintBill from "./PrintBill";
import ReprintKOT from "./ReprintKOT";
import data from "../../../../../Operation/data.json";
import OrderPaymentModal from "./OrderPaymentModal";

const OrderContent = ({
  selectedTable,
  setSelectedTable,
  KOTByTable,
  customerDetails,
  setCustomerDetails,
  updateItemQuantity,
  addItemToMerged,
  removeItemFromKOT,
  kotcurrent,
  total,
  cgst,
  sgst,
  serviceTax,
  institutionData,
  activeTab,
  printAllItems,
  setPrintAllItems,
  printKOT,
  printBill,
  setShowModal,
  saveKOT,
  settleBill,
  billItems,
  localBillItems,
  setLocalBillItems,
  fetchTableData,
  bluetoothConnected,
  bluetoothDeviceName,
  connectBluetooth,
  printBluetooth,
  currentTableData,
  fetchPendingOrders,
  splitBillMode, 
  setSplitBillMode, 
  orderGroups, 
  setOrderGroups, 
  selectedOrderId,
  setSelectedOrderId, 
  toggleSplitBillMode,
  showReprintKOT, setShowReprintKOT

}) => {
  const { printerType, printerpaperSize } = data;
  const [showOrderPaymentModal, setShowOrderPaymentModal] = useState(false);
  const { setLoader } = useContext(Context);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printMode, setPrintMode] = useState("local");
  const [showPrintBill, setShowPrintBill] = useState(false);

  const [movingItem, setMovingItem] = useState(null);
  const [targetOrderId, setTargetOrderId] = useState(null);

  const totalKotQuantity = (kotcurrent?.items || kotcurrent?.billItems || []).reduce(
    (sum, item) => sum + item.quantity,
    0
  );
  const screenWidth = window.innerWidth;

  useEffect(() => {
    setLocalBillItems(billItems || []);
  }, [billItems]);

  useEffect(() => {
    if (bluetoothConnected) {
      setPrintMode("bluetooth");
    }
  }, [bluetoothConnected]);

  const getKOTNumber = (tableId) => {
    const today = new Date().toISOString().split("T")[0];
    let kotData = JSON.parse(localStorage.getItem("kotOrders")) || { date: today, lastKOT: 0, kotTables: {} };
    if (kotData.date !== today) {
      kotData = { date: today, lastKOT: 0, kotTables: {} };
    }
    if (kotData.kotTables[tableId]) {
      return kotData.kotTables[tableId];
    }
    const newKOT = kotData.lastKOT + 1;
    kotData.kotTables[tableId] = newKOT;
    kotData.lastKOT = newKOT;
    localStorage.setItem("kotOrders", JSON.stringify(kotData));
    return newKOT;
  };

  const formatKOT = () => {
    const formatDateTime = () => {
      const now = new Date();
      return now.toLocaleString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }).replace(",", "");
    };
    const orderType = !isNaN(selectedTable?.id) ? "Dine In" : selectedTable?.id.toLowerCase().startsWith("takeaway") ? "Takeaway" : "Dine In";
    const kotNo = getKOTNumber(selectedTable?.id) || "N/A";
    const items = kotcurrent?.items || [];
    const totalQty = items.reduce((sum, item) => sum + item.quantity, 0);

    const lineWidth = printerpaperSize === "80" ? 48 : 32;
    const itemNameLength = lineWidth - 5;

    const headerFont = printerpaperSize === "80" ? "\x1B\x4D\x00" : "\x1B\x4D\x00";
    const itemFont = printerpaperSize === "80" ? "\x1B\x4D\x00" : "\x1B\x4D\x01";
    const resetFont = "\x1D\x21\x00";

    const centerText = (text, width = lineWidth) => {
      const textLength = text.length;
      if (textLength >= width) return text.substring(0, width);
      const padding = Math.floor((width - textLength) / 2);
      return " ".repeat(padding) + text + " ".repeat(padding + (textLength % 2));
    };

    let kot = `\x1B\x61\x01${headerFont}`;
    kot += `${centerText(`KOT No: ${kotNo}`)}\n`;
    kot += `${centerText(orderType)}\n`;
    kot += `${centerText(`Date: ${formatDateTime()}`)}\n`;
    kot += `${centerText(`Table: ${selectedTable?.id || "N/A"}`)}\n`;
    kot += `${resetFont}${itemFont}${centerText(`-`.repeat(lineWidth))}\n`;
    kot += `Item${" ".repeat(lineWidth - 4 - 3)}Qty\n`;
    kot += `${centerText(`-`.repeat(lineWidth))}\n`;

    items.forEach((item) => {
      const itemName = item.itemName || item.name;
      const qty = item.quantity.toString().padStart(4, " ");
      if (itemName.length <= itemNameLength) {
        kot += `${itemName.padEnd(itemNameLength)}${qty}\n`;
      } else {
        const chunks = itemName.match(new RegExp(`.{1,${itemNameLength}}`, "g"));
        chunks.forEach((chunk, index) => {
          if (index === 0) {
            kot += `${chunk.padEnd(itemNameLength)}${qty}\n`;
          } else {
            kot += `${chunk.padEnd(itemNameLength)}${" ".repeat(4)}\n`;
          }
        });
      }
    });

    kot += `${centerText(`-`.repeat(lineWidth))}\n`;
    kot += `${centerText(`Total Qty: ${totalQty}`)}\n`;
    kot += `${centerText(`-`.repeat(lineWidth))}\n`;
    kot += `${resetFont}\n\n\n`;
    return kot;
  };

  const centerText = (text, width) => {
    const textLength = text.length;
    if (textLength >= width) return text.substring(0, width);
    const padding = Math.floor((width - textLength) / 2);
    return " ".repeat(padding) + text + " ".repeat(width - textLength - padding);
  };

  const formatBill = (orderItems, customerId) => {
    const lineWidth = printerpaperSize === "80" ? 48 : 32;
    const itemNameLength = Math.floor(lineWidth * 0.55);
    const qtyLength = 4;
    const rateLength = 6;
    const totalLength = lineWidth - itemNameLength - qtyLength - rateLength - 3;

    const headerFont = printerpaperSize === "80" ? "\x1B\x4D\x00" : "\x1B\x4D\x00";
    const itemFont = printerpaperSize === "80" ? "\x1B\x4D\x00" : "\x1B\x4D\x01";
    const resetFont = "\x1D\x21\x00";

    const justifyBetween = (leftText, rightText, width = lineWidth) => {
      const totalLength = leftText.length + rightText.length;
      if (totalLength >= width) return `${leftText}${rightText}`.substring(0, width);
      const spaceBetween = width - totalLength;
      return `${leftText}${" ".repeat(spaceBetween)}${rightText}`;
    };

    const formatDateTime = () => {
      const now = new Date();
      return now.toLocaleString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }).replace(",", "");
    };

    const items = orderItems || localBillItems;
    const billSubtotal = items.reduce((sum, item) => {
      const price = Number(item.price) || 0;
      const quantity = Number(item.quantity) || 0;
      return sum + price * quantity;
    }, 0);

    const cgstRate = 2.5;
    const sgstRate = 2.5;
    const serviceTaxRate = institutionData?.serviceTax !== undefined ? institutionData.serviceTax : 0;
    const calculatedCgst = (billSubtotal * cgstRate) / 100;
    const calculatedSgst = (billSubtotal * sgstRate) / 100;
    const calculatedServiceTax = (billSubtotal * serviceTaxRate) / 100;

    let calculatedTotal;
    if (institutionData.cgst === 0 || institutionData.sgst === 0) {
      calculatedTotal = Math.round(billSubtotal);
    } else {
      calculatedTotal = Math.round(billSubtotal + calculatedCgst + calculatedSgst + calculatedServiceTax);
    }

    const totalQty = items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);

    let bill = `\x1B\x61\x01${headerFont}`;
    bill += `${institutionData?.companyName || "The Chai Theka"}\n`;
    bill += `${institutionData?.Query_Address || "BhawaniPeth"}\n`;
    bill += `Contact: ${institutionData?.Query_PhoneNumber || "+917008120727"}\n`;
    bill += `Date: ${formatDateTime()}\n`;
    bill += `${institutionData?.gstin ? `GSTIN: ${institutionData.gstin}\n` : ""}`;
    bill += `Table: ${selectedTable?.id || "N/A"}\n`;

    if (splitBillMode && customerId) {
      bill += `Order ID: ${customerId}\n`;
    }

    bill += `${resetFont}${itemFont}${centerText(`-`.repeat(lineWidth))}\n`;
    bill += `Item${" ".repeat(itemNameLength - 4)}Qty Rate Total\n`;
    bill += `${centerText(`-`.repeat(lineWidth))}\n`;

    items.forEach((item) => {
      const itemName = (item.itemName || item.name || "Unknown").substring(0, itemNameLength).padEnd(itemNameLength, " ");
      const qty = (Number(item.quantity) || 0).toString().padStart(qtyLength, " ");
      const price = Number(item.price) || 0;
      const rate = (price % 1 === 0 ? price : price.toFixed(2)).toString().padStart(rateLength, " ");
      const totalValue = price * (Number(item.quantity) || 0);
      const total = (totalValue % 1 === 0 ? totalValue : totalValue.toFixed(2)).toString().padStart(totalLength, " ");
      const line = `${itemName}${qty}${rate}${total}`;
      bill += `${line}\n`;

      if ((item.itemName || item.name || "").length > itemNameLength) {
        const remaining = (item.itemName || item.name || "").substring(itemNameLength);
        const chunks = remaining.match(new RegExp(`.{1,${itemNameLength}}`, "g")) || [];
        chunks.forEach(chunk => {
          bill += `${chunk.padEnd(itemNameLength)}${" ".repeat(lineWidth - itemNameLength)}\n`;
        });
      }
    });

    bill += `${resetFont}${centerText(`-`.repeat(lineWidth))}\n`;
    bill += `${justifyBetween("Total Qty:", `${totalQty}`)}\n`;
    if (institutionData.cgst !== 0 && institutionData.sgst !== 0) {
      bill += `${justifyBetween("Subtotal:", `${billSubtotal.toFixed(2)}`)}\n`;
    }
    bill += `${justifyBetween(`CGST (${cgstRate}%):`, `${calculatedCgst.toFixed(2)}`)}\n`;
    bill += `${justifyBetween(`SGST (${sgstRate}%):`, `${calculatedSgst.toFixed(2)}`)}\n`;
    if (serviceTaxRate > 0) {
      bill += `${justifyBetween(`Service Tax (${serviceTaxRate}%):`, `${calculatedServiceTax.toFixed(2)}`)}\n`;
    }
    bill += `${justifyBetween("Total (Rounded):", `${calculatedTotal.toFixed(2)}`)}\n`;
    bill += `${centerText(`-`.repeat(lineWidth))}\n`;
    bill += `Thanks for visiting!\n`;
    bill += `\x1B\x34`;
    bill += `Empower your business with tekkzy.com\n`;
    bill += `\x1B\x35`;
    bill += `${resetFont}\n\n\n`;

    return bill;
  };

  const handlePrintKOT = async () => {
    if (!kotcurrent?.items?.length) {
      console.log("No items in KOT.");
      toast.info("No items to print.");
      return;
    }

    if (!selectedTable) {
      console.error("Selected table is undefined.");
      toast.error("No table selected.");
      return;
    }

    const kotData = formatKOT();

    if (printMode === "bluetooth" && bluetoothConnected) {
      await saveKOT(selectedTable);
      console.log("Attempting Bluetooth print for KOT...");
      printBluetooth(kotData);
    } else {
      console.log("Attempting local print for KOT...");
      printKOT(selectedTable, kotData);
    }
  };

  const handlePrintBill = async () => {
    if (splitBillMode) {
      if (!selectedOrderId) {
        toast.error("Please select an order to print.");
        return;
      }

      const selectedOrder = orderGroups.find(group => group.orderId === selectedOrderId);
      if (!selectedOrder || !selectedOrder.items.length) {
        toast.error("No items to print in selected order.");
        return;
      }

      await saveBill(selectedTable, selectedOrder.items, selectedOrderId);

      if (printMode === "bluetooth" && bluetoothConnected) {
        const billData = formatBill(selectedOrder.items, selectedOrderId);
        console.log("Attempting Bluetooth print for split Bill...");
        await printBluetooth(billData);
      } else {
        console.log("Attempting local print for split Bill...");
        printBill({ table: selectedTable, items: selectedOrder.items, orderId: selectedOrderId });
        setShowPrintBill(true);
      }
    } else {
      if (!localBillItems.length) {
        toast.error("No items to print in bill.");
        return;
      }

      if (!selectedTable) {
        console.error("Selected table is undefined.");
        toast.error("No table selected.");
        return;
      }

      await saveBill(selectedTable);

      if (printMode === "bluetooth" && bluetoothConnected) {
        const billData = formatBill();
        console.log("Attempting Bluetooth print for Bill...");
        await printBluetooth(billData);
      } else {
        const billData = formatBill();
        console.log("Attempting local print for Bill...");
        printBill({ table: selectedTable, items: localBillItems });
        setShowPrintBill(true);
      }
    }
  };

  const handleReprintKOT = async () => {
 
    if (!selectedTable) {
      console.error("Selected table is undefined.");
      toast.error("No table selected.");
      return;
    }


   
      setShowReprintKOT(true);
    
  };


  const saveBill = async (selectedTable, orderItems, orderId) => {
    if (!selectedTable) {
      console.error("Selected table is undefined in saveBill.");
      toast.error("No table selected.");
      return;
    }

    const tableIdStr = String(selectedTable.id);
    setLoader(true);
    try {
      const endpoint = orderId
        ? `/admin/orderId-save/${institutionData.institution}/${tableIdStr}/${orderId}`
        : `/admin/orders-items/${institutionData.institution}/${tableIdStr}`;

      const items = orderItems || localBillItems;

      await API.put("main", endpoint, {
        body: {
          items,
          serviceTaxPercentage: institutionData?.serviceTax !== undefined ? institutionData.serviceTax : 0,
          cgstPercentage: institutionData?.cgst !== undefined ? institutionData.cgst : 2.5,
          sgstPercentage: institutionData?.sgst !== undefined ? institutionData.sgst : 2.5,
        },
      });

      if (orderId) {
        console.log("Order ID exists, skipping saveBill API calls.");
        setLoader(false);
        return;
      }
      const billEndpoint = orderId
        ? `/admin/orders-bill/${institutionData.institution}/${tableIdStr}/${orderId}`
        : `/admin/orders-bill/${institutionData.institution}/${tableIdStr}`;

      await API.post("main", billEndpoint, {
        body: {
          items,
        },
      });

      setLoader(false);
      await fetchTableData(tableIdStr);
    } catch (error) {
      setLoader(false);
      console.error("Error saving bill:", error);
      toast.error("Failed to save bill. Please try again.");
    }
  };

  const handleSettleBill = async () => {
    if (splitBillMode) {
      if (!selectedOrderId) {
        toast.error("Please select an order to settle.");
        return;
      }
      const selectedOrder = orderGroups.find(group => group.orderId === selectedOrderId);

      await saveBill(selectedTable, selectedOrder.items, selectedOrderId);

      setShowOrderPaymentModal(true);
    } else {
      settleBill();
    }
  };

  const moveItemBetweenOrders = async (item, fromOrderId, toOrderId, quantity) => {
    if (!fromOrderId || !toOrderId) {
      toast.error("Please select source and target orders.");
      return;
    }

    const moveQty = Math.min(quantity || 1, item.quantity);

    setOrderGroups(prevGroups => {
      const updatedGroups = prevGroups.map(group => {
        if (group.orderId === fromOrderId) {
          if (moveQty >= item.quantity) {
            return {
              ...group,
              items: group.items.filter(i => i.itemId !== item.itemId)
            };
          } else {
            return {
              ...group,
              items: group.items.map(i =>
                i.itemId === item.itemId ? { ...i, quantity: i.quantity - moveQty } : i
              )
            };
          }
        }

        if (group.orderId === toOrderId) {
          const existingItem = group.items.find(i => i.itemId === item.itemId);
          if (existingItem) {
            return {
              ...group,
              items: group.items.map(i =>
                i.itemId === item.itemId ? { ...i, quantity: i.quantity + moveQty } : i
              )
            };
          }
          return {
            ...group,
            items: [...group.items, { ...item, quantity: moveQty }]
          };
        }
        return group;
      });
      return updatedGroups;
    });

    setLoader(true);
    try {
      const tableIdStr = String(selectedTable.id);

      await API.post(
        "main",
        `/admin/move-items/${institutionData.institution}/${tableIdStr}`,
        {
          body: {
            sourceOrderId: fromOrderId,
            destinationOrderId: toOrderId,
            items: [{ 
              itemId: item.itemId, 
              quantity: moveQty,  
              price: item.price, 
              itemName: item.itemName || item.name 
            }],
            serviceTaxPercentage: institutionData?.serviceTax !== undefined ? institutionData.serviceTax : 0,
            cgstPercentage: institutionData?.cgst !== undefined ? institutionData.cgst : 2.5,
            sgstPercentage: institutionData?.sgst !== undefined ? institutionData.sgst : 2.5
          }
        }
      );

      await fetchPendingOrders();
      setMovingItem(null);
      setTargetOrderId(null);
      toast.success(`${moveQty} item${moveQty > 1 ? 's' : ''} moved successfully`);
      setLoader(false);
    } catch (error) {
      setLoader(false);
      console.error("Error moving item between orders:", error);
      toast.error("Failed to move item. Please try again.");
    }
  };

  const handleUpdateItemQuantity = (tableId, itemId, newQuantity) => {
    if (splitBillMode) {
      setOrderGroups(prevGroups =>
        prevGroups.map(group => {
          if (group.orderId === selectedOrderId) {
            const updatedItems = group.items
              .map(item =>
                item.itemId === itemId ? { ...item, quantity: newQuantity } : item
              )
              .filter(item => item.quantity > 0);
            return { ...group, items: updatedItems };
          }
          return group;
        })
      );
    } else {
      updateItemQuantity(tableId, itemId, newQuantity);
      const updatedItems = localBillItems
        .map(item =>
          item.itemId === itemId ? { ...item, quantity: newQuantity } : item
        )
        .filter(item => item.quantity > 0);
      setLocalBillItems(updatedItems);
    }
  };

  const handleRemoveItemFromKOT = (tableId, itemId) => {
    if (splitBillMode) {
      setOrderGroups(prevGroups =>
        prevGroups.map(group => {
          if (group.orderId === selectedOrderId) {
            const updatedItems = group.items.filter(item => item.itemId !== itemId);
            return { ...group, items: updatedItems };
          }
          return group;
        })
      );
    } else {
      removeItemFromKOT(tableId, itemId);
      const updatedItems = localBillItems.filter(item => item.itemId !== itemId);
      setLocalBillItems(updatedItems);
    }
  };

  const billSubtotal = localBillItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const cgstRate = 2.5;
  const sgstRate = 2.5;
  const serviceTaxRate = institutionData?.serviceTax !== undefined ? institutionData.serviceTax : 0;

  const calculatedCgst = (billSubtotal * cgstRate) / 100;
  const calculatedSgst = (billSubtotal * sgstRate) / 100;
  const calculatedServiceTax = (billSubtotal * serviceTaxRate) / 100;

  let calculatedTotal;
  if (institutionData.cgst === 0 || institutionData.sgst === 0) {
    calculatedTotal = Math.round(billSubtotal);
  } else {
    calculatedTotal = Math.round(billSubtotal + calculatedCgst + calculatedSgst + calculatedServiceTax);
  }

  const ItemControls = ({ item, orderId }) => (
    <div key={item.itemId} className="flex items-center justify-between p-3 border-b group">
      <div className="flex-1">
        <div className="flex justify-between">
          <p className="font-medium text-sm">{item.itemName || item.name}</p>
          {(splitBillMode && activeTab !== "KOT" && orderGroups.length > 1) ? (
            <button
              onClick={() => {
                setMovingItem(item);
                setTargetOrderId(null);
              }}
              className="text-blue-500 transition-opacity"
              title="Move item to another order"
            >
              <MoveRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={() => handleRemoveItemFromKOT(selectedTable?.id, item.itemId)}
              className="text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="flex items-center justify-between mt-1">
          <div className="flex items-center">
            <button
              onClick={() => handleUpdateItemQuantity(selectedTable?.id, item.itemId, item.quantity - 1)}
              className="w-6 h-6 flex items-center justify-center border rounded"
            >
              -
            </button>
            <span className="mx-2 text-sm">{item.quantity}</span>
            <button
              onClick={() => handleUpdateItemQuantity(selectedTable?.id, item.itemId, item.quantity + 1)}
              className="w-6 h-6 flex items-center justify-center border rounded"
            >
              +
            </button>
          </div>
          <span className="text-sm">₹{(item.price * item.quantity).toFixed(2)}</span>
        </div>
        {splitBillMode && orderId && (
          <div className="mt-1">
            <span className="text-xs text-gray-500">Order ID: {orderId}</span>
          </div>
        )}
      </div>
    </div>
  );

  const PrintModal = () => (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg relative">
        <button
          onClick={() => setShowPrintModal(false)}
          className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
          aria-label="Close"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
        <h2 className="text-lg font-semibold mb-4">Select Printing Method</h2>
        <p className="mb-4">How would you like to handle printing?</p>
        <div className="flex gap-4">
          <button
            onClick={() => {
              connectBluetooth();
              setPrintMode("bluetooth");
              setShowPrintModal(false);
            }}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Bluetooth Printer
          </button>
          <button
            onClick={() => {
              setPrintMode("local");
              setShowPrintModal(false);
            }}
            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
          >
ternoons
            Wired Printer
          </button>
        </div>
      </div>
    </div>
  );

  const MoveItemModal = () => {
    if (!movingItem) return null;

    const otherOrders = orderGroups.filter(group => group.orderId !== selectedOrderId);
    const [moveQuantity, setMoveQuantity] = useState(1);
    const maxQuantity = movingItem.quantity;

    useEffect(() => {
      if (otherOrders.length === 1 && !targetOrderId) {
        setTargetOrderId(otherOrders[0].orderId);
      }
    }, [otherOrders, targetOrderId]);

    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg shadow-lg relative max-w-md w-full">
          <button
            onClick={() => setMovingItem(null)}
            className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
            aria-label="Close"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
          <h2 className="text-lg font-semibold mb-4">Move Item to Another Order</h2>
          <p className="mb-2">Moving: {movingItem.itemName || movingItem.name}</p>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Quantity to Move:</label>
            <div className="flex items-center">
              <button
                onClick={() => setMoveQuantity(prev => Math.max(1, prev - 1))}
                className="w-8 h-8 flex items-center justify-center border rounded-l"
                disabled={moveQuantity <= 1}
              >
                -
              </button>
              <input
                type="number"
                min="1"
                max={maxQuantity}
                value={moveQuantity}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  if (!isNaN(val) && val >= 1 && val <= maxQuantity) {
                    setMoveQuantity(val);
                  }
                }}
                className="w-16 text-center border-t border-b h-8"
              />
              <button
                onClick={() => setMoveQuantity(prev => Math.min(maxQuantity, prev + 1))}
                className="w-8 h-8 flex items-center justify-center border rounded-r"
                disabled={moveQuantity >= maxQuantity}
              >
                +
              </button>
              <span className="ml-2 text-sm text-gray-500">of {maxQuantity} available</span>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">Select Target Order:</label>
            <select
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              value={targetOrderId || ""}
              onChange={(e) => setTargetOrderId(e.target.value)}
            >
              <option value="">Select an order</option>
              {otherOrders.map(order => (
                <option key={order.orderId} value={order.orderId}>
                  Order {order.orderId} - {order.items.length} items
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-end gap-3">
            <button
              onClick={() => setMovingItem(null)}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={() => moveItemBetweenOrders(movingItem, selectedOrderId, targetOrderId, moveQuantity)}
              disabled={!targetOrderId}
              className={`px-4 py-2 ${targetOrderId ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300'} border border-transparent rounded-md shadow-sm text-sm font-medium text-white`}
            >
              Move {moveQuantity} Item{moveQuantity > 1 ? 's' : ''}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderOrderTabs = () => {
    if (!splitBillMode || !orderGroups.length) return null;

    return (
      <div className="flex border-b overflow-x-auto py-2 px-2">
        {orderGroups.map((group) => (
          <button
            key={group.orderId}
            className={`px-3 py-1 mr-2 text-xs rounded-md whitespace-nowrap ${
              selectedOrderId === group.orderId
                ? 'bg-blue-500 text-white'
                : 'bg-gray-200 text-gray-700'
            }`}
            onClick={() => setSelectedOrderId(group.orderId)}
          >
            Order #{group.orderId} ({group.items.length} items)
          </button>
        ))}
      </div>
    );
  };

  const renderContent = () => {
    if (activeTab === "KOT") {
      const hasKOTItems = (kotcurrent?.items || []).length > 0;
      return (
        <>
          <div className="flex-1 overflow-y-auto p-2 sm:p-4 max-h-[30rem] max406:max-h-[15rem]">
            {(kotcurrent?.items || []).map((item) => (
              <ItemControls key={item.itemId} item={item} />
            ))}
          </div>
          <div className="p-2 sm:p-4 border-b space-y-2">
            <div className="flex gap-2">
              <div className="relative w-1/2">
                <User className="w-4 h-4 absolute left-2 top-2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Customer Name"
                  className="w-full pl-8 pr-2 py-1 text-xs sm:text-sm border rounded"
                  value={customerDetails[selectedTable?.id]?.name || ""}
                  onChange={(e) =>
                    setCustomerDetails((prev) => ({
                      ...prev,
                      [selectedTable?.id]: {
                        ...prev[selectedTable?.id],
                        name: e.target.value,
                      },
                    }))
                  }
                />
              </div>
              <div className="relative w-1/2">
                <Phone className="w-4 h-4 absolute left-2 top-2 text-gray-400" />
                <input
                  type="tel"
                  placeholder="Phone Number"
                  className="w-full pl-8 pr-2 py-1 text-xs sm:text-sm border rounded"
                  value={customerDetails[selectedTable?.id]?.phone || ""}
                  onChange={(e) =>
                    setCustomerDetails((prev) => ({
                      ...prev,
                      [selectedTable?.id]: {
                        ...prev[selectedTable?.id],
                        phone: e.target.value,
                      },
                    }))
                  }
                />
              </div>
            </div>
          </div>
          <div className="p-4 border-t space-y-2">
            <div className="flex justify-between font-bold text-sm sm:text-base mt-3 mb-3">
              <span>Total Items:</span>
              <span>{totalKotQuantity}</span>
            </div>
            {printerType !== "local" && (
              <button
                onClick={() => setShowPrintModal(true)}
                className="w-full py-2 bg-blue-500 text-white rounded-lg text-xs sm:text-sm"
              >
                Change Printer Mode
              </button>
            )}
            <button
              onClick={handlePrintKOT}
              className={`w-full py-2 ${hasKOTItems ? "bg-green-500" : "bg-gray-400"} text-white rounded-lg text-xs sm:text-sm mb-2`}
              disabled={!hasKOTItems}
            >
              Print KOT
            </button>
            {printerType !== "local" && (
              <p className={`text-sm ${
                printMode === "local" || (printMode === "bluetooth" && bluetoothConnected)
                  ? "text-green-500"
                  : "text-red-500"
              }`}>
                {printMode === "local"
                  ? "Print Mode: Local Print"
                  : printMode === "bluetooth" && bluetoothConnected
                  ? `Print Mode: Bluetooth - Using device: ${bluetoothDeviceName}`
                  : "Print Mode: Bluetooth - Not connected"}
              </p>
            )}
          </div>
          {showPrintModal && <PrintModal />}
        </>
      );
    } else {
      if (splitBillMode) {
        const selectedOrder = orderGroups.find(group => group.orderId === selectedOrderId);
        const orderItems = selectedOrder?.items || [];

        const orderSubtotal = orderItems.reduce((sum, item) => {
          const price = Number(item.price) || 0;
          const quantity = Number(item.quantity) || 0;
          return sum + price * quantity;
        }, 0);

        const orderCgst = (orderSubtotal * cgstRate) / 100;
        const orderSgst = (orderSubtotal * sgstRate) / 100;
        const orderServiceTax = (orderSubtotal * serviceTaxRate) / 100;

        let orderTotal;
        if (institutionData.cgst === 0 || institutionData.sgst === 0) {
          orderTotal = Math.round(orderSubtotal);
        } else {
          orderTotal = Math.round(orderSubtotal + orderCgst + orderSgst + orderServiceTax);
        }

        return (
          <>
            {renderOrderTabs()}
            <div className="flex-1 overflow-y-auto p-2 sm:p-4 max-h-[19rem] max406:max-h-[10rem]">
              {selectedOrderId ? (
                orderItems.length > 0 ? (
                  orderItems.map((item) => (
                    <ItemControls
                      key={item.itemId}
                      item={item}
                      orderId={selectedOrderId}
                    />
                  ))
                ) : (
                  <p className="text-sm text-gray-500">No items in this order</p>
                )
              ) : (
                <p className="text-sm text-gray-500">Please select an order</p>
              )}
            </div>
            <div className="p-2 sm:p-4 border-b space-y-2">
              <div className="flex gap-2">
                <div className="relative w-1/2">
                  <User className="w-4 h-4 absolute left-2 top-2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Customer Name"
                    className="w-full pl-8 pr-2 py-1 text-xs sm:text-sm border rounded"
                    value={selectedOrder?.customerName || ""}
                    disabled={true}
                  />
                </div>
                <div className="relative w-1/2">
                  <Phone className="w-4 h-4 absolute left-2 top-2 text-gray-400" />
                  <input
                    type="tel"
                    placeholder="Phone Number"
                    className="w-full pl-8 pr-2 py-1 text-xs sm:text-sm border rounded"
                    value={selectedOrder?.phoneNumber || ""}
                    disabled={true}
                  />
                </div>
              </div>
            </div>
            <div className="border-t p-4 space-y-2">
              {institutionData.cgst !== 0 && institutionData.sgst !== 0 && (
                <div className="flex justify-between text-xs sm:text-sm">
                  <span>Subtotal:</span>
                  <span>₹{orderSubtotal.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-xs sm:text-sm">
                <span>CGST ({cgstRate}%):</span>
                <span>₹{orderCgst.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-xs sm:text-sm">
                <span>SGST ({sgstRate}%):</span>
                <span>₹{orderSgst.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-xs sm:text-sm">
                <span>Service Tax ({serviceTaxRate}%):</span>
                <span>₹{orderServiceTax.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-bold text-sm sm:text-base border-t">
                <span>Total:</span>
                <span>₹{orderTotal.toFixed(2)}</span>
              </div>
            </div>
            <div className="p-4 mb-10 space-y-2">
              <button
                onClick={handlePrintBill}
                className={`w-full py-2 ${selectedOrderId && orderItems.length > 0 ? "bg-blue-500" : "bg-gray-400"} text-white rounded-lg text-xs sm:text-sm`}
                disabled={!selectedOrderId || orderItems.length === 0}
                >
                Print Selected Order Bill
              </button>
              {printerType !== "local" && (
                <p className={`text-sm ${
                  printMode === "local" || (printMode === "bluetooth" && bluetoothConnected)
                  ? "text-green-500"
                  : "text-red-500"
                }`}>
                  {printMode === "local"
                    ? "Print Mode: Local Print"
                    : printMode === "bluetooth" && bluetoothConnected
                    ? `Print Mode: Bluetooth - Using device: ${bluetoothDeviceName}`
                    : "Print Mode: Bluetooth - Not connected"}
                </p>
              )}
              <button
                onClick={handleSettleBill}
                className={`w-full py-2 ${selectedOrderId && orderItems.length > 0 ? "bg-green-500" : "bg-gray-400"} text-white rounded-lg text-xs sm:text-sm`}
                disabled={!selectedOrderId || orderItems.length === 0}
                >
                Settle Selected Order
              </button>
                {institutionData.institution==="bhuk2323"&&(
  
                <button
                  onClick={handleReprintKOT}
                  className={`w-full py-2 ${orderItems.length > 0 ? "bg-yellow-500" : "bg-gray-400"} text-white rounded-lg text-xs sm:text-sm`}
                  disabled={!selectedOrderId || orderItems.length === 0}
                >
                  Reprint KOT
                </button>
                )
  
                }
            </div>
            {movingItem && <MoveItemModal />}
            {showReprintKOT && (
              <ReprintKOT
                table={selectedTable}
                customerDetails={customerDetails}
                billItems={orderItems}
                institutionData={institutionData}
                saveKOT={saveKOT}
                screenWidth={screenWidth}
                tableData={currentTableData}
                printData={printBluetooth}
                connected={bluetoothConnected}
                setShowReprintKOT={setShowReprintKOT}
              />
            )}
          </>
        );
      } else {
        const hasItems = localBillItems.length > 0;
        return (
          <>
            <div className="flex-1 overflow-y-auto p-2 sm:p-4 max-h-[19rem] max406:max-h-[10rem]">
              {localBillItems.length > 0 ? (
                localBillItems.map((item) => <ItemControls key={item.itemId} item={item} />)
              ) : (
                <p className="text-sm text-gray-500">No bill items available</p>
              )}
            </div>
            <div className="p-2 sm:p-4 border-b space-y-2">
              <div className="flex gap-2">
                <div className="relative w-1/2">
                  <User className="w-4 h-4 absolute left-2 top-2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Customer Name"
                    className="w-full pl-8 pr-2 py-1 text-xs sm:text-sm border rounded"
                    value={customerDetails[selectedTable?.id]?.name || ""}
                    onChange={(e) =>
                      setCustomerDetails((prev) => ({
                        ...prev,
                        [selectedTable?.id]: {
                          ...prev[selectedTable?.id],
                          name: e.target.value,
                        },
                      }))
                    }
                  />
                </div>
                <div className="relative w-1/2">
                  <Phone className="w-4 h-4 absolute left-2 top-2 text-gray-400" />
                  <input
                    type="tel"
                    placeholder="Phone Number"
                    className="w-full pl-8 pr-2 py-1 text-xs sm:text-sm border rounded"
                    value={customerDetails[selectedTable?.id]?.phone || ""}
                    onChange={(e) =>
                      setCustomerDetails((prev) => ({
                        ...prev,
                        [selectedTable?.id]: {
                          ...prev[selectedTable?.id],
                          phone: e.target.value,
                        },
                      }))
                    }
                  />
                </div>
              </div>
            </div>
            <div className="border-t p-4 space-y-2">
              {institutionData.cgst !== 0 && institutionData.sgst !== 0 && (
                <div className="flex justify-between text-xs sm:text-sm">
                  <span>Subtotal:</span>
                  <span>₹{billSubtotal.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-xs sm:text-sm">
                <span>CGST ({cgstRate}%):</span>
                <span>₹{calculatedCgst.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-xs sm:text-sm">
                <span>SGST ({sgstRate}%):</span>
                <span>₹{calculatedSgst.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-xs sm:text-sm">
                <span>Service Tax ({serviceTaxRate}%):</span>
                <span>₹{calculatedServiceTax.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-bold text-sm sm:text-base border-t">
                <span>Total:</span>
                <span>₹{calculatedTotal.toFixed(2)}</span>
              </div>
            </div>
            <div className="p-4 mb-10 space-y-2">
              <button
                onClick={handlePrintBill}
                className={`w-full py-2 ${hasItems ? "bg-blue-500" : "bg-gray-400"} text-white rounded-lg text-xs sm:text-sm`}
                disabled={!hasItems}
                >
                Print Bill
              </button>
              {printerType !== "local" && (
                <p className={`text-sm ${
                  printMode === "local" || (printMode === "bluetooth" && bluetoothConnected)
                  ? "text-green-500"
                  : "text-red-500"
                }`}>
                  {printMode === "local"
                    ? "Print Mode: Local Print"
                    : printMode === "bluetooth" && bluetoothConnected
                    ? `Print Mode: Bluetooth - Using device: ${bluetoothDeviceName}`
                    : "Print Mode: Bluetooth - Not connected"}
                </p>
              )}
              <button
                onClick={handleSettleBill}
                className={`w-full py-2 ${hasItems ? "bg-green-500" : "bg-gray-400"} text-white rounded-lg text-xs sm:text-sm`}
                disabled={!hasItems}
                >
                Settle Payment
              </button>
                {institutionData.institution==="bhuk2323"&&(
              <button
                onClick={handleReprintKOT}
                className={`w-full py-2 ${hasItems ? "bg-yellow-500" : "bg-gray-400"} text-white rounded-lg text-xs sm:text-sm`}
                disabled={!hasItems}
              >
                Reprint KOT
              </button>
              )
  
              }
            </div>
            {showReprintKOT && (
              <ReprintKOT
                table={selectedTable}
                customerDetails={customerDetails}
                billItems={localBillItems}
                institutionData={institutionData}
                saveKOT={saveKOT}
                screenWidth={screenWidth}
                tableData={currentTableData}
                printData={printBluetooth}
                connected={bluetoothConnected}
                setShowReprintKOT={setShowReprintKOT}
              />
            )}
          </>
        );
      }
    }
  };

  return (
    <>
      <div className="flex-1 flex flex-col">{renderContent()}</div>
      {showOrderPaymentModal && (
        <OrderPaymentModal
          selectedTable={selectedTable}
          institutionData={institutionData}
          onClose={() => setShowOrderPaymentModal(false)}
          selectedOrderId={splitBillMode ? selectedOrderId : null}
          fetchTableData={fetchTableData}
          fetchPendingOrders={fetchPendingOrders}
          customerDetails={customerDetails}
        />
      )}
    </>
  );
};

export default OrderContent;